import React, { useCallback, useEffect, useState } from "react";
import { api, API_URL } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Video, Clock, Play, ShieldAlert, AlertTriangle } from "lucide-react";

const STATUS_OPTS = [
  { v: "pending",  label: "Pending"  },
  { v: "approved", label: "Approved" },
  { v: "rejected", label: "Rejected" },
];

/**
 * Video-only verification queue. The list endpoint /admin/verifications
 * strips private paths — a separate audited call to /admin/verifications/{vid}
 * is required to view the actual video, so we defer the video load until the
 * moderator explicitly clicks "Load video".
 */
export default function AdminVerifications() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loadingVideoId, setLoadingVideoId] = useState(null);
  const [videoMap, setVideoMap] = useState({}); // { vid: signedUrl }
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/admin/verifications?status=${status}`);
      setRows(Array.isArray(data.verifications) ? data.verifications : []);
    } catch (e) {
      const status_code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setError({ status: status_code, detail: typeof detail === "string" ? detail : String(detail || e.message || "Failed to load verifications") });
      // eslint-disable-next-line no-console
      console.warn("[AdminVerifications] load:", status_code, detail);
    } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const loadVideo = async (vid) => {
    setLoadingVideoId(vid);
    try {
      const { data } = await api.get(`/admin/verifications/${vid}`);
      const p = data.verification?.video_path;
      setVideoMap((m) => ({ ...m, [vid]: p ? `${API_URL}/files/${p}` : null }));
    } catch (e) {
      console.warn("[AdminVerifications] loadVideo:", e);
      toast.error("Could not load video");
    } finally { setLoadingVideoId(null); }
  };

  const decide = async (vid, decision, r) => {
    try {
      await api.post(`/admin/verifications/${vid}/decide`, {
        decision, rejection_reason: r || null,
      });
      toast.success(decision === "approve" ? "Verification approved" : "Verification rejected");
      setRejectingId(null); setReason("");
      load();
    } catch (e) {
      console.warn("[AdminVerifications] decide:", e);
      toast.error("Action failed");
    }
  };

  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.22em] text-amber-400">Trust</p>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif text-stone-50 mt-1">Verifications</h1>
          <p className="text-sm text-stone-400 mt-1">Video verification queue · every load is audit-logged.</p>
        </div>
        <div className="flex items-center gap-2">
          {STATUS_OPTS.map(o => (
            <button
              key={o.v}
              onClick={() => setStatus(o.v)}
              data-testid={`adm-ver-filter-${o.v}`}
              className={`px-3 py-1.5 rounded-full text-xs border ${status === o.v
                ? "bg-amber-500/15 border-amber-500/40 text-amber-200"
                : "border-stone-700 text-stone-300 hover:border-stone-500"}`}
            >{o.label}</button>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200 flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 flex-none" />
        Verification videos are private. Only admin &amp; moderator can view them, and every view is logged.
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-lg border border-rose-500/40 bg-rose-500/5 text-xs text-rose-200 flex items-start gap-2" data-testid="adm-ver-error">
          <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
          <div>
            <p className="font-medium">Could not load verifications (HTTP {error.status ?? "?"})</p>
            <p className="text-rose-300/80 mt-0.5">{error.detail}</p>
          </div>
        </div>
      )}
      {loading && (<p className="mt-4 text-sm text-stone-400">Loading…</p>)}

      <div className="mt-4 space-y-3" data-testid="adm-ver-list">
        {rows.map(v => (
          <div key={v.id} className="rounded-xl border border-stone-800 bg-stone-950/60 p-4" data-testid={`adm-ver-row-${v.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm text-stone-100">User <span className="text-stone-400">{v.user_id?.slice(0, 8)}…</span></p>
                <p className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" /> Submitted {new Date(v.created_at).toLocaleString()}</p>
                {v.profile_id && <p className="text-[11px] text-stone-500 mt-0.5">Profile {v.profile_id.slice(0, 8)}…</p>}
                {v.submitter_notes && <p className="text-xs text-stone-300 mt-2 p-2 rounded bg-stone-900/50 border border-stone-800"><strong>Notes:</strong> {v.submitter_notes}</p>}
              </div>
              {status === "pending" && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => decide(v.id, "approve")}
                    className="px-3 py-2 rounded-lg bg-emerald-500 text-stone-950 text-xs font-semibold hover:bg-emerald-400 inline-flex items-center gap-1"
                    data-testid={`adm-ver-approve-${v.id}`}
                  ><Check className="w-3.5 h-3.5" /> Approve</button>
                  <button
                    onClick={() => setRejectingId(v.id)}
                    className="px-3 py-2 rounded-lg border border-rose-500/50 text-rose-300 text-xs hover:bg-rose-500/10 inline-flex items-center gap-1"
                    data-testid={`adm-ver-reject-${v.id}`}
                  ><X className="w-3.5 h-3.5" /> Reject</button>
                </div>
              )}
            </div>

            <div className="mt-4 rounded-lg bg-black/60 border border-stone-800 min-h-[180px] flex items-center justify-center overflow-hidden">
              {videoMap[v.id] ? (
                <video controls src={videoMap[v.id]} className="w-full max-h-[420px]" data-testid={`adm-ver-video-${v.id}`} />
              ) : (
                <button
                  onClick={() => loadVideo(v.id)}
                  disabled={loadingVideoId === v.id}
                  className="flex flex-col items-center gap-2 px-5 py-4 text-stone-300 hover:text-amber-300 disabled:opacity-50"
                  data-testid={`adm-ver-load-${v.id}`}
                >
                  <div className="w-12 h-12 rounded-full bg-stone-900 border border-stone-700 flex items-center justify-center">
                    {loadingVideoId === v.id ? <Video className="w-5 h-5 animate-pulse" /> : <Play className="w-5 h-5" />}
                  </div>
                  <span className="text-xs">{loadingVideoId === v.id ? "Loading…" : "Load video (audited)"}</span>
                </button>
              )}
            </div>

            {rejectingId === v.id && (
              <div className="mt-3 p-3 rounded-lg border border-rose-500/40 bg-rose-500/5">
                <label className="text-[11px] uppercase tracking-wider text-rose-200">Rejection reason</label>
                <textarea
                  value={reason} onChange={(e) => setReason(e.target.value)}
                  rows={2} placeholder="Be specific so the user can resubmit correctly"
                  className="mt-1.5 w-full bg-stone-900/60 border border-stone-700 rounded px-3 py-2 text-sm text-stone-100"
                  data-testid={`adm-ver-reason-${v.id}`}
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => reason.trim() && decide(v.id, "reject", reason)}
                    className="px-3 py-2 rounded bg-rose-500 text-stone-950 text-xs font-semibold hover:bg-rose-400"
                    data-testid={`adm-ver-reject-confirm-${v.id}`}
                  >Confirm reject</button>
                  <button
                    onClick={() => { setRejectingId(null); setReason(""); }}
                    className="px-3 py-2 rounded border border-stone-700 text-stone-300 text-xs"
                  >Cancel</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && (
          <p className="text-sm text-stone-500 text-center p-8">No verifications with status <em>{status}</em>.</p>
        )}
      </div>
    </div>
  );
}
